import "./CashRegisterDashboard.scss";

import {
  faArrowDown,
  faArrowsRotate,
  faCashRegister,
  faClockRotateLeft,
  faLock,
  faPlus,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";

import AppLoading from "../../commons/AppLoading";
import EmptyState from "../../commons/EmptyState";
import HallAppToast from "../../commons/HallAppToast";
import MenuTitle from "../../commons/menu/MenuTitle";
import CashRegisterAPI from "./CashRegisterAPI";

const cashRegisterAPI = new CashRegisterAPI();

const money = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value) || 0);

const dateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

const decimalValue = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const raw = String(value).trim();
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const errorMessage = (error) => {
  const data = error?.response?.data;
  if (Array.isArray(data)) {
    return data[0]?.message || data[0]?.description || "Não foi possível concluir a operação.";
  }
  return data?.message || data?.description || "Não foi possível concluir a operação.";
};

const CashRegisterDashboard = () => {
  const [registers, setRegisters] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [form, setForm] = useState({});

  const loadData = useCallback(async (manual = false) => {
    manual ? setRefreshing(true) : setLoading(true);
    try {
      const [registerResponse, openingResponse] = await Promise.all([
        cashRegisterAPI.findRegisters(),
        cashRegisterAPI.findOpenings(),
      ]);
      setRegisters(Array.isArray(registerResponse.data) ? registerResponse.data : []);
      setOpenings(Array.isArray(openingResponse.data) ? openingResponse.data : []);
    } catch (error) {
      HallAppToast.error(errorMessage(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openByRegister = useMemo(() => {
    const result = {};
    openings
      .filter((opening) => opening.status === "OPEN")
      .forEach((opening) => {
        result[opening.cashRegisterId] = opening;
      });
    return result;
  }, [openings]);

  const activeRegisters = useMemo(
    () => registers.filter((register) => register.active),
    [registers]
  );

  const closedOpenings = useMemo(
    () => openings.filter((opening) => opening.status !== "OPEN").slice(0, 8),
    [openings]
  );

  const closeModal = () => {
    if (saving) return;
    setModal(null);
    setSelected(null);
    setSummary(null);
    setMovements([]);
    setForm({});
  };

  const showCreateRegister = () => {
    setForm({ name: "Caixa principal" });
    setModal("register");
  };

  const showOpen = (register) => {
    setSelected(register);
    setForm({ openingBalance: "0,00", description: "" });
    setModal("open");
  };

  const showWithdrawal = (opening) => {
    setSelected(opening);
    setForm({ amount: "", description: "" });
    setModal("withdrawal");
  };

  const showClose = async (opening) => {
    setSelected(opening);
    setForm({ countedBalance: "", description: "" });
    setSummary(null);
    setModal("close");
    setModalLoading(true);
    try {
      const response = await cashRegisterAPI.closingSummary(opening.id);
      setSummary(response.data);
    } catch (error) {
      HallAppToast.error(errorMessage(error));
      setModal(null);
    } finally {
      setModalLoading(false);
    }
  };

  const showMovements = async (opening) => {
    setSelected(opening);
    setMovements([]);
    setModal("movements");
    setModalLoading(true);
    try {
      const response = await cashRegisterAPI.movements(opening.id);
      setMovements(response.data?.content || []);
    } catch (error) {
      HallAppToast.error(errorMessage(error));
      setModal(null);
    } finally {
      setModalLoading(false);
    }
  };

  const saveRegister = async (event) => {
    event.preventDefault();
    if (!form.name?.trim()) return HallAppToast.error("Informe um nome para o caixa.");
    setSaving(true);
    try {
      await cashRegisterAPI.createRegister({
        name: form.name.trim(),
        type: "SHARED",
        responsibleEmployeeId: null,
        defaultShared: !registers.some((register) => register.defaultShared),
        active: true,
      });
      HallAppToast.successWithMessage("Caixa criado.");
      closeModalAfterSave();
    } catch (error) {
      HallAppToast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const saveOpening = async (event) => {
    event.preventDefault();
    const openingBalance = decimalValue(form.openingBalance);
    if (openingBalance === null || openingBalance < 0) {
      return HallAppToast.error("Informe um valor inicial válido.");
    }
    setSaving(true);
    try {
      await cashRegisterAPI.open({
        cashRegisterId: selected.id,
        openingBalance,
        description: form.description?.trim() || null,
      });
      HallAppToast.successWithMessage("Caixa aberto e pronto para uso.");
      closeModalAfterSave();
    } catch (error) {
      HallAppToast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const saveWithdrawal = async (event) => {
    event.preventDefault();
    const amount = decimalValue(form.amount);
    if (amount === null || amount <= 0) return HallAppToast.error("Informe o valor retirado.");
    if (!form.description?.trim()) return HallAppToast.error("Informe o motivo da retirada.");
    setSaving(true);
    try {
      await cashRegisterAPI.withdraw(selected.id, {
        amount,
        description: form.description.trim(),
      });
      HallAppToast.successWithMessage("Retirada registrada.");
      closeModalAfterSave();
    } catch (error) {
      HallAppToast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const counted = decimalValue(form.countedBalance);
  const difference = counted === null || !summary
    ? null
    : (Math.round(counted * 100) - Math.round(Number(summary.expectedBalance || 0) * 100)) / 100;

  const saveClosing = async (event) => {
    event.preventDefault();
    if (counted === null || counted < 0) return HallAppToast.error("Informe quanto foi contado.");
    if (difference !== 0 && !form.description?.trim()) {
      return HallAppToast.error("Conte brevemente por que o valor está diferente.");
    }
    setSaving(true);
    try {
      await cashRegisterAPI.close(
        selected.id,
        counted,
        form.description?.trim() || null
      );
      HallAppToast.successWithMessage("Caixa fechado com sucesso.");
      closeModalAfterSave();
    } catch (error) {
      HallAppToast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const closeModalAfterSave = () => {
    setModal(null);
    setSelected(null);
    setSummary(null);
    setForm({});
    loadData(true);
  };

  return (
    <div className="container cash-register-page">
      <MenuTitle title="Caixa" />

      <section className="cash-register-page__hero">
        <div>
          <span className="cash-register-page__eyebrow">Dinheiro do estabelecimento</span>
          <h1>Controle seu caixa sem complicação</h1>
          <p>Veja o saldo, registre retiradas e confira o dinheiro antes de fechar.</p>
        </div>
        <div className="cash-register-page__hero-actions">
          <Button variant="light" onClick={() => loadData(true)} disabled={refreshing}>
            <FontAwesomeIcon icon={faArrowsRotate} spin={refreshing} />
            <span>Atualizar</span>
          </Button>
          <Button variant="outline-light" onClick={showCreateRegister}>
            <FontAwesomeIcon icon={faPlus} /> Outro caixa
          </Button>
        </div>
      </section>

      {loading ? (
        <div className="cash-register-page__loading"><AppLoading /></div>
      ) : activeRegisters.length === 0 ? (
        <div className="cash-register-page__empty">
          <EmptyState
            title="Vamos preparar seu primeiro caixa?"
            description="Crie um caixa para começar a registrar as entradas e retiradas do estabelecimento."
            icon={<FontAwesomeIcon icon={faCashRegister} />}
          />
          <Button onClick={showCreateRegister}><FontAwesomeIcon icon={faPlus} /> Criar meu caixa</Button>
        </div>
      ) : (
        <Row className="g-3 mb-4">
          {activeRegisters.map((register) => {
            const opening = openByRegister[register.id];
            return (
              <Col xs={12} lg={6} key={register.id}>
                <article className={`cash-register-card ${opening ? "is-open" : ""}`}>
                  <header>
                    <div className="cash-register-card__icon"><FontAwesomeIcon icon={faCashRegister} /></div>
                    <div>
                      <h2>{register.name}</h2>
                      <span>{register.defaultShared ? "Caixa principal" : "Caixa compartilhado"}</span>
                    </div>
                    <Badge bg={opening ? "success" : "secondary"}>{opening ? "Aberto" : "Fechado"}</Badge>
                  </header>

                  {opening ? (
                    <>
                      <div className="cash-register-card__balance">
                        <span>Deve haver no caixa agora</span>
                        <strong>{money(opening.currentBalance)}</strong>
                        <small>Aberto em {dateTime(opening.openedAt)} com {money(opening.openingBalance)}</small>
                      </div>
                      <div className="cash-register-card__actions">
                        <Button variant="outline-secondary" onClick={() => showMovements(opening)}>
                          <FontAwesomeIcon icon={faReceipt} /> Movimentações
                        </Button>
                        <Button variant="outline-danger" onClick={() => showWithdrawal(opening)}>
                          <FontAwesomeIcon icon={faArrowDown} /> Retirar dinheiro
                        </Button>
                        <Button variant="primary" onClick={() => showClose(opening)}>
                          <FontAwesomeIcon icon={faLock} /> Conferir e fechar
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="cash-register-card__closed">
                      <p>Este caixa está fechado. Informe o dinheiro inicial para começar o dia.</p>
                      <Button onClick={() => showOpen(register)}>Abrir caixa</Button>
                    </div>
                  )}
                </article>
              </Col>
            );
          })}
        </Row>
      )}

      {!loading && closedOpenings.length > 0 && (
        <section className="cash-register-history">
          <h2><FontAwesomeIcon icon={faClockRotateLeft} /> Fechamentos recentes</h2>
          <div className="cash-register-history__list">
            {closedOpenings.map((opening) => (
              <button key={opening.id} type="button" onClick={() => showMovements(opening)}>
                <div><strong>{opening.cashRegisterName}</strong><span>{dateTime(opening.closedAt || opening.canceledAt)}</span></div>
                <div className="text-end">
                  <strong>{opening.status === "CANCELED" ? "Cancelado" : money(opening.countedBalance)}</strong>
                  {opening.status === "CLOSED" && (
                    <span className={Number(opening.balanceDifference) === 0 ? "text-success" : "text-warning"}>
                      {Number(opening.balanceDifference) === 0 ? "Valor conferido" : `Diferença de ${money(opening.balanceDifference)}`}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      <Modal show={modal === "register"} onHide={closeModal} centered>
        <Form onSubmit={saveRegister}>
          <Modal.Header closeButton><Modal.Title>Criar outro caixa</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="text-muted">Use um nome fácil de reconhecer, como “Recepção” ou “Caixa principal”.</p>
            <Form.Group>
              <Form.Label>Nome do caixa</Form.Label>
              <Form.Control autoFocus maxLength={100} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="light" onClick={closeModal}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? "Criando..." : "Criar caixa"}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={modal === "open"} onHide={closeModal} centered>
        <Form onSubmit={saveOpening}>
          <Modal.Header closeButton><Modal.Title>Abrir {selected?.name}</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="text-muted">Conte somente o dinheiro que já está na gaveta antes de começar.</p>
            <Form.Group className="mb-3">
              <Form.Label>Quanto há no caixa agora?</Form.Label>
              <Form.Control autoFocus inputMode="decimal" value={form.openingBalance || ""} onChange={(e) => setForm({ ...form, openingBalance: e.target.value })} />
            </Form.Group>
            <Form.Group><Form.Label>Observação <span className="text-muted">(opcional)</span></Form.Label><Form.Control as="textarea" rows={2} maxLength={500} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="light" onClick={closeModal}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? "Abrindo..." : "Abrir caixa"}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={modal === "withdrawal"} onHide={closeModal} centered>
        <Form onSubmit={saveWithdrawal}>
          <Modal.Header closeButton><Modal.Title>Retirar dinheiro</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="cash-register-modal-balance"><span>Disponível no caixa</span><strong>{money(selected?.currentBalance)}</strong></div>
            <Form.Group className="mb-3"><Form.Label>Quanto foi retirado?</Form.Label><Form.Control autoFocus inputMode="decimal" placeholder="0,00" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Por que o dinheiro foi retirado?</Form.Label><Form.Control as="textarea" rows={2} maxLength={500} placeholder="Ex.: dinheiro enviado para o cofre" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="light" onClick={closeModal}>Cancelar</Button><Button variant="danger" type="submit" disabled={saving}>{saving ? "Registrando..." : "Confirmar retirada"}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={modal === "close"} onHide={closeModal} centered>
        <Form onSubmit={saveClosing}>
          <Modal.Header closeButton><Modal.Title>Conferir e fechar o caixa</Modal.Title></Modal.Header>
          <Modal.Body>
            {modalLoading || !summary ? <div className="py-4 text-center"><Spinner /></div> : (
              <>
                <div className="cash-register-closing-summary">
                  <div><span>Valor inicial</span><strong>{money(summary.openingBalance)}</strong></div>
                  <div><span>Entradas</span><strong className="text-success">{money(summary.receiptTotal)}</strong></div>
                  <div><span>Estornos e retiradas</span><strong className="text-danger">{money(Number(summary.reversalTotal || 0) + Number(summary.withdrawalTotal || 0))}</strong></div>
                  <div className="total"><span>Deveria haver</span><strong>{money(summary.expectedBalance)}</strong></div>
                </div>
                <Form.Group className="mb-3"><Form.Label>Quanto você contou no caixa?</Form.Label><Form.Control autoFocus inputMode="decimal" placeholder="0,00" value={form.countedBalance || ""} onChange={(e) => setForm({ ...form, countedBalance: e.target.value })} /></Form.Group>
                {difference !== null && (
                  <div className={`cash-register-difference ${difference === 0 ? "is-exact" : "is-different"}`}>
                    {difference === 0 ? "Tudo certo: o valor confere." : difference < 0 ? `Está faltando ${money(Math.abs(difference))}.` : `Está sobrando ${money(difference)}.`}
                  </div>
                )}
                {difference !== null && difference !== 0 && <Form.Group className="mt-3"><Form.Label>O que pode ter acontecido?</Form.Label><Form.Control as="textarea" rows={2} maxLength={500} placeholder="Escreva uma explicação curta" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Form.Group>}
              </>
            )}
          </Modal.Body>
          <Modal.Footer><Button variant="light" onClick={closeModal}>Voltar</Button><Button type="submit" disabled={saving || modalLoading}>{saving ? "Fechando..." : "Confirmar fechamento"}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={modal === "movements"} onHide={closeModal} centered scrollable>
        <Modal.Header closeButton><Modal.Title>Movimentações de {selected?.cashRegisterName}</Modal.Title></Modal.Header>
        <Modal.Body>
          {modalLoading ? <div className="py-4 text-center"><Spinner /></div> : movements.length === 0 ? <p className="text-muted text-center py-4">Nenhuma movimentação registrada.</p> : (
            <div className="cash-register-movements">
              {movements.map((movement) => {
                const withdrawal = movement.type === "WITHDRAWAL";
                const reversal = movement.type === "REVERSAL";
                return <div key={movement.id}><span className={`cash-register-movements__icon ${movement.amount < 0 ? "negative" : "positive"}`}><FontAwesomeIcon icon={withdrawal ? faArrowDown : faReceipt} /></span><div><strong>{withdrawal ? "Retirada" : reversal ? "Pagamento estornado" : movement.customerName ? `Pagamento de ${movement.customerName}` : "Pagamento recebido"}</strong><small>{movement.description || dateTime(movement.createDate)}</small></div><strong className={movement.amount < 0 ? "text-danger" : "text-success"}>{money(movement.amount)}</strong></div>;
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button onClick={closeModal}>Fechar</Button></Modal.Footer>
      </Modal>

      <div className="cash-register-page__footer-space" />
    </div>
  );
};

export default CashRegisterDashboard;
